import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useIsAdmin } from '../../hooks/useIsAdmin';
import {
  deleteAdminPayment,
  deleteAdminPayout,
  loadAdminReferrals,
  recordAdminPayment,
  recordAdminPayout,
  updateReferralSettings,
  type AdminPendingReferral,
  type AdminReferralPayment,
  type AdminReferralPayout,
  type AdminReferrerStats,
} from '../../containers/AdminReferralsPage/reducer';
import {
  selectAdminReferralsLoading,
  selectAdminReferralsOverview,
} from '../../containers/AdminReferralsPage/selectors';
import { AdminConfirmButton, AdminLoading, AdminNotice, AdminTable } from '../components/V2AdminUI';
import './referrals.css';

type Column<T> = {
  key: string;
  title: React.ReactNode;
  render: (row: T) => React.ReactNode;
};

type ReferralTableProps<T> = {
  title: string;
  columns: Column<T>[];
  rows: readonly T[];
  rowKey: keyof T;
  minWidth: number;
  loading: boolean;
};

const ReferralTable = <T,>({
  title,
  rows,
  columns,
  rowKey,
  minWidth,
  loading,
}: ReferralTableProps<T>) => {
  const [requestedPage, setRequestedPage] = React.useState(1);
  const pageSize = 20;
  const page = Math.min(requestedPage, Math.max(1, Math.ceil(rows.length / pageSize)));

  return (
    <section className="settings-panel v2-admin-table-panel">
      <div className="settings-panel__head">
        <h2>{title}</h2>
      </div>
      <AdminTable<T>
        caption={title}
        rows={rows.slice((page - 1) * pageSize, page * pageSize)}
        columns={columns}
        rowKey={rowKey}
        minWidth={minWidth}
        loading={loading}
        pagination={{ page, pageSize, total: rows.length, onChange: setRequestedPage }}
      />
    </section>
  );
};

const formatMoney = (value: number) => `$${Number(value).toFixed(2)}`;
const formatDate = (value: string) => dayjs(value).format('DD.MM.YY HH:mm');
const parseAmount = (value: string) => parseFloat(value.replace(',', '.'));
const userLink = (id: number, email: string) => (
  <Link className="v2-admin-user-link" to={`/users/${id}`}>
    {email}
  </Link>
);

const V2Referrals: React.FC = () => {
  useIsAdmin();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const overview = useSelector(selectAdminReferralsOverview);
  const loading = useSelector(selectAdminReferralsLoading);
  const [requested, setRequested] = React.useState(false);
  const [defaultPercent, setDefaultPercent] = React.useState('10');
  const [payerEmail, setPayerEmail] = React.useState('');
  const [referrerEmail, setReferrerEmail] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [paymentPercent, setPaymentPercent] = React.useState('10');
  const [note, setNote] = React.useState('');
  const [payoutReferrerEmail, setPayoutReferrerEmail] = React.useState('');
  const [payoutAmount, setPayoutAmount] = React.useState('');
  const [payoutNote, setPayoutNote] = React.useState('');
  const [paymentError, setPaymentError] = React.useState(false);
  const [payoutError, setPayoutError] = React.useState(false);

  React.useEffect(() => {
    setRequested(true);
    dispatch(loadAdminReferrals());
  }, [dispatch]);

  React.useEffect(() => {
    if (overview?.referralPercent !== undefined) {
      setDefaultPercent(String(overview.referralPercent));
      setPaymentPercent(String(overview.referralPercent));
    }
  }, [overview?.referralPercent]);

  const saveDefaultPercent = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const percent = Number(defaultPercent);
    if (loading || !Number.isFinite(percent) || percent < 0 || percent > 100) return;
    dispatch(updateReferralSettings(percent));
  };

  const recordPayment = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    const parsedAmount = parseAmount(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setPaymentError(true);
      return;
    }
    const commissionPercent = Number(paymentPercent);
    if (!Number.isFinite(commissionPercent) || commissionPercent < 0 || commissionPercent > 100) {
      return;
    }
    dispatch(
      recordAdminPayment({
        payerEmail: payerEmail.trim(),
        referrerEmail: referrerEmail.trim() || undefined,
        amount: parsedAmount,
        commissionPercent,
        note: note.trim() || undefined,
      }),
    );
    setAmount('');
    setNote('');
    setPaymentError(false);
  };

  const recordPayout = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    const parsedAmount = parseAmount(payoutAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setPayoutError(true);
      return;
    }
    dispatch(
      recordAdminPayout({
        referrerEmail: payoutReferrerEmail.trim(),
        amount: parsedAmount,
        note: payoutNote.trim() || undefined,
      }),
    );
    setPayoutAmount('');
    setPayoutNote('');
    setPayoutError(false);
  };

  const balanceColumns: Column<AdminReferrerStats>[] = [
    {
      key: 'referrer',
      title: t('admin_referrals_referrer'),
      render: (row) => userLink(row.referrerId, row.referrerEmail),
    },
    {
      key: 'count',
      title: t('admin_referrals_referrals_count'),
      render: (row) => row.referralsCount,
    },
    {
      key: 'earned',
      title: t('admin_referrals_earned'),
      render: (row) => formatMoney(row.totalEarned),
    },
    {
      key: 'paid',
      title: t('admin_referrals_paid_out'),
      render: (row) => formatMoney(row.totalPaidOut),
    },
    {
      key: 'balance',
      title: t('admin_referrals_balance'),
      render: (row) => (
        <span className="v2-admin-money v2-admin-money--highlight">{formatMoney(row.balance)}</span>
      ),
    },
  ];
  const paymentColumns: Column<AdminReferralPayment>[] = [
    { key: 'date', title: t('referral_date'), render: (row) => formatDate(row.createdAt) },
    {
      key: 'referrer',
      title: t('admin_referrals_referrer'),
      render: (row) =>
        row.referrerEmail
          ? row.referrerId
            ? userLink(row.referrerId, row.referrerEmail)
            : row.referrerEmail
          : '—',
    },
    {
      key: 'payer',
      title: t('admin_referrals_referral'),
      render: (row) => userLink(row.payerId, row.payerEmail),
    },
    { key: 'amount', title: t('admin_referrals_amount'), render: (row) => formatMoney(row.amount) },
    { key: 'percent', title: '%', render: (row) => `${Number(row.commissionPercent)}%` },
    {
      key: 'commission',
      title: t('admin_referrals_commission'),
      render: (row) => formatMoney(row.commissionAmount),
    },
    { key: 'note', title: t('admin_referrals_note'), render: (row) => row.note || '—' },
    {
      key: 'actions',
      title: '',
      render: (row) => (
        <AdminConfirmButton
          className="v2-admin-link-danger"
          title={t('admin_referrals_delete_payment_confirm')}
          confirmLabel={t('delete')}
          cancelLabel={t('cancel')}
          disabled={loading}
          onConfirm={() => dispatch(deleteAdminPayment(row.id))}
        >
          {t('delete')}
        </AdminConfirmButton>
      ),
    },
  ];
  const payoutColumns: Column<AdminReferralPayout>[] = [
    { key: 'date', title: t('referral_date'), render: (row) => formatDate(row.createdAt) },
    {
      key: 'referrer',
      title: t('admin_referrals_referrer'),
      render: (row) => userLink(row.referrerId, row.referrerEmail),
    },
    { key: 'amount', title: t('admin_referrals_amount'), render: (row) => formatMoney(row.amount) },
    { key: 'note', title: t('admin_referrals_note'), render: (row) => row.note || '—' },
    {
      key: 'actions',
      title: '',
      render: (row) => (
        <AdminConfirmButton
          className="v2-admin-link-danger"
          title={t('admin_referrals_delete_payout_confirm')}
          confirmLabel={t('delete')}
          cancelLabel={t('cancel')}
          disabled={loading}
          onConfirm={() => dispatch(deleteAdminPayout(row.id))}
        >
          {t('delete')}
        </AdminConfirmButton>
      ),
    },
  ];
  const pendingColumns: Column<AdminPendingReferral>[] = [
    {
      key: 'referrer',
      title: t('admin_referrals_referrer'),
      render: (row) => userLink(row.referrerId, row.referrerEmail),
    },
    {
      key: 'referral',
      title: t('admin_referrals_referral'),
      render: (row) => userLink(row.userId, row.email),
    },
    { key: 'payments', title: t('admin_referrals_payments_count'), render: () => 0 },
    { key: 'commission', title: t('admin_referrals_commission'), render: () => formatMoney(0) },
  ];

  if (!overview) {
    return loading || !requested ? (
      <AdminLoading label={t('referral_loading')} />
    ) : (
      <AdminNotice
        type="error"
        action={
          <button
            type="button"
            className="outline-button"
            onClick={() => dispatch(loadAdminReferrals())}
          >
            {t('admin_referrals_retry')}
          </button>
        }
      >
        {t('admin_referrals_unavailable')}
      </AdminNotice>
    );
  }

  return (
    <div className="v2-admin-referrals" aria-busy={loading}>
      <section
        className="kpi-grid v2-admin-referrals-summary"
        aria-label={t('admin_referrals_summary')}
      >
        <div className="kpi">
          <p className="kpi-label">{t('admin_referrals_default_percent')}</p>
          <p className="kpi-value">
            {overview.referralPercent}
            <small>%</small>
          </p>
        </div>
        <div className="kpi">
          <p className="kpi-label">{t('admin_referrals_total_paid')}</p>
          <p className="kpi-value positive">{formatMoney(overview.totalPaid)}</p>
        </div>
        <div className="kpi">
          <p className="kpi-label">{t('admin_referrals_total_commission')}</p>
          <p className="kpi-value">{formatMoney(overview.totalCommission)}</p>
        </div>
        <div className="kpi">
          <p className="kpi-label">{t('admin_referrals_total_paid_out')}</p>
          <p className="kpi-value">{formatMoney(overview.totalPaidOut)}</p>
        </div>
        <div className="kpi">
          <p className="kpi-label">{t('admin_referrals_pending')}</p>
          <p className="kpi-value">{overview.pendingReferrals.length}</p>
        </div>
      </section>

      <form className="settings-panel v2-admin-form-panel" onSubmit={saveDefaultPercent}>
        <div className="settings-panel__head">
          <h2>{t('admin_referrals_default_percent')}</h2>
        </div>
        <div className="v2-admin-inline-form">
          <label className="settings-field v2-admin-inline-form__field">
            <span className="settings-field-label">{t('referral_percent')}</span>
            <input
              className="v2-admin-input"
              type="number"
              min="0"
              max="100"
              step="any"
              required
              disabled={loading}
              value={defaultPercent}
              onChange={(event) => setDefaultPercent(event.target.value)}
            />
          </label>
          <button type="submit" className="acid-button" disabled={loading}>
            {t('admin_referrals_save')}
          </button>
        </div>
      </form>

      <form className="settings-panel v2-admin-form-panel" onSubmit={recordPayment}>
        <div className="settings-panel__head">
          <h2>{t('admin_referrals_record_payment')}</h2>
        </div>
        <p className="settings-field-hint">{t('admin_referrals_manual_referrer_hint')}</p>
        <div className="v2-admin-payment-grid">
          <label className="settings-field">
            <span className="settings-field-label">{t('admin_referrals_payer_email')}</span>
            <input
              className="v2-admin-input"
              type="email"
              placeholder="user@example.com"
              required
              disabled={loading}
              value={payerEmail}
              onChange={(event) => setPayerEmail(event.target.value)}
            />
          </label>
          <label className="settings-field">
            <span className="settings-field-label">{t('admin_referrals_referrer_email')}</span>
            <input
              className="v2-admin-input"
              type="email"
              placeholder="referrer@example.com"
              disabled={loading}
              value={referrerEmail}
              onChange={(event) => setReferrerEmail(event.target.value)}
            />
          </label>
          <label className="settings-field">
            <span className="settings-field-label">{t('admin_referrals_amount')}</span>
            <input
              className="v2-admin-input"
              inputMode="decimal"
              placeholder="30"
              required
              disabled={loading}
              value={amount}
              aria-invalid={paymentError || undefined}
              aria-describedby={paymentError ? 'referral-payment-error' : undefined}
              onChange={(event) => {
                setAmount(event.target.value);
                setPaymentError(false);
              }}
            />
          </label>
          <label className="settings-field">
            <span className="settings-field-label">{t('admin_referrals_commission_percent')}</span>
            <input
              className="v2-admin-input"
              type="number"
              min="0"
              max="100"
              step="any"
              required
              disabled={loading}
              value={paymentPercent}
              onChange={(event) => setPaymentPercent(event.target.value)}
            />
          </label>
          <label className="settings-field v2-admin-payment-grid__wide">
            <span className="settings-field-label">{t('admin_referrals_note')}</span>
            <input
              className="v2-admin-input"
              placeholder={t('admin_referrals_optional')}
              disabled={loading}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
        </div>
        {paymentError ? (
          <p id="referral-payment-error" className="v2-admin-field-error" role="alert">
            {t('admin_referrals_invalid_amount')}
          </p>
        ) : null}
        <div className="settings-panel__footer">
          <button type="submit" className="acid-button" disabled={loading}>
            {t('admin_referrals_record_payment')}
          </button>
        </div>
      </form>

      <form className="settings-panel v2-admin-form-panel" onSubmit={recordPayout}>
        <div className="settings-panel__head">
          <h2>{t('admin_referrals_record_payout_title')}</h2>
        </div>
        <div className="v2-admin-payment-grid">
          <label className="settings-field">
            <span className="settings-field-label">{t('admin_referrals_referrer_email')}</span>
            <input
              className="v2-admin-input"
              type="email"
              placeholder="referrer@example.com"
              required
              disabled={loading}
              value={payoutReferrerEmail}
              onChange={(event) => setPayoutReferrerEmail(event.target.value)}
            />
          </label>
          <label className="settings-field">
            <span className="settings-field-label">{t('admin_referrals_payout_amount')}</span>
            <input
              className="v2-admin-input"
              inputMode="decimal"
              placeholder="50"
              required
              disabled={loading}
              value={payoutAmount}
              aria-invalid={payoutError || undefined}
              aria-describedby={payoutError ? 'referral-payout-error' : undefined}
              onChange={(event) => {
                setPayoutAmount(event.target.value);
                setPayoutError(false);
              }}
            />
          </label>
          <label className="settings-field v2-admin-payment-grid__wide">
            <span className="settings-field-label">{t('admin_referrals_note')}</span>
            <input
              className="v2-admin-input"
              placeholder={t('admin_referrals_optional')}
              disabled={loading}
              value={payoutNote}
              onChange={(event) => setPayoutNote(event.target.value)}
            />
          </label>
        </div>
        {payoutError ? (
          <p id="referral-payout-error" className="v2-admin-field-error" role="alert">
            {t('admin_referrals_invalid_amount')}
          </p>
        ) : null}
        <div className="settings-panel__footer">
          <button type="submit" className="acid-button" disabled={loading}>
            {t('admin_referrals_record_payout')}
          </button>
        </div>
      </form>

      <ReferralTable
        title={t('admin_referrals_balances')}
        rows={overview.referrers}
        columns={balanceColumns}
        rowKey="referrerId"
        minWidth={720}
        loading={loading}
      />
      <ReferralTable
        title={t('admin_referrals_payments')}
        rows={overview.payments}
        columns={paymentColumns}
        rowKey="id"
        minWidth={980}
        loading={loading}
      />
      <ReferralTable
        title={t('admin_referrals_payouts')}
        rows={overview.payouts}
        columns={payoutColumns}
        rowKey="id"
        minWidth={760}
        loading={loading}
      />
      <ReferralTable
        title={t('admin_referrals_pending_title')}
        rows={overview.pendingReferrals}
        columns={pendingColumns}
        rowKey="userId"
        minWidth={640}
        loading={loading}
      />
    </div>
  );
};

export default V2Referrals;
